/**
 * Which menus a bar list can carry a per-menu price on (design 2026-09-30
 * Part B), and which per-menu prices could never ring.
 *
 * WHERE THE PRICE LIVES. A per-menu price for an option is a
 * `ModifierGroupReference.modifierPriceOverrides` entry on a MENU GROUP's
 * `modifierGroups[]`. The till (`resolveParentPriceOverrides`), online ordering
 * and the assistant (`resolveModifierPriceOverrides`) walk the ordered item's
 * `menuGroupPath` ROOT→LEAF and take the FIRST reference that carries
 * overrides for that modifier group — so a price on a menu applies to every
 * item below it, unless a menu ABOVE it on that path carries its own.
 *
 * WHY "LISTS NO ITEMS DIRECTLY" (B3). A menu group's `modifierGroups[]` entry
 * does a second thing: it ATTACHES that modifier group to every item the menu
 * group lists directly (POS `getAllModifierGroupRefsForItem`). A price on a
 * group that lists the beers would put the vodka list on the beers. So only a
 * group with `items.length === 0` is offered — at House of Pho the menus
 * (Dine-In, Lunch, Happy Hour, Dinner) and the folders above the bar's items.
 *
 * The hierarchy is walked by `walkMenuPaths` — the till's `parentIds` walk,
 * never `subgroups` (menuTree.ts). An archived group is not on any menu; an
 * INACTIVE one still is offered, so a manager can price a menu before it opens.
 *
 * Pure: no Firestore, no clock.
 */
import type { IMenuGroup } from './MenuGroup';
import type { IModifierGroupReference } from './ModifierGroupReference';
import { walkMenuPaths } from './menuTree';

/** How a menu's place in the tree is written: "Dine-In Menu › Happy Hour". */
export const MENU_PATH_SEPARATOR = ' › ';

/** A menu a bar list can be priced on. */
export interface EligiblePriceMenu {
  readonly menuGroupId: number;
  readonly name: string;
  /** Its first path from a root, in walk order, e.g. "Dine-In Menu › Happy Hour". */
  readonly path: string;
  /**
   * Every root path that reaches it below which a bar item hangs. A group with
   * several parents (House of Pho's DRINKS sits under Lunch, Happy Hour and
   * Dinner) has one per parent — a price there rings on all of them.
   */
  readonly paths: readonly string[];
}

/**
 * A menu on a path to a bar item that is NOT offered, and why. `lists_items`:
 * it lists items directly, so a price there would also attach the list to
 * them (B3). The listing group itself is never reported — it holds the bar
 * item, so it is the drink's own group, not a menu.
 */
export interface ExcludedPriceMenu {
  readonly menuGroupId: number;
  readonly name: string;
  /** Its first path from a root, e.g. "Dine-In Menu › Lunch Menu". */
  readonly path: string;
  readonly reason: 'lists_items';
}

/** Archived groups are on no menu. Inactive ones are still priceable. */
const notArchived = (group: IMenuGroup): boolean => group.isArchived !== true;

/**
 * The menus a per-menu bar price can be set on: every group on some ROOT→LEAF
 * path to one of `targetItemIds` (the items carrying a bar-generated list)
 * that lists NO items directly. Returned in walk order (a root, then its
 * descendants by tree order), each once.
 */
export function eligiblePriceMenus(
  menuGroups: readonly IMenuGroup[],
  targetItemIds: Iterable<number>,
): EligiblePriceMenu[] {
  return priceMenuEligibility(menuGroups, targetItemIds).eligible;
}

/**
 * ONE walk, both answers: the menus a per-menu bar price can be set on
 * (`eligiblePriceMenus`), and the menus on those same paths that are left out
 * only because they list items directly (`excluded`) — so the page can say
 * why a menu is missing instead of leaving it to be guessed. Both in walk
 * order, each once.
 */
export function priceMenuEligibility(
  menuGroups: readonly IMenuGroup[],
  targetItemIds: Iterable<number>,
): { eligible: EligiblePriceMenu[]; excluded: ExcludedPriceMenu[] } {
  const targets = new Set(targetItemIds);
  if (targets.size === 0) return { eligible: [], excluded: [] };

  /** Paths (ids + names) that END at a group listing a target item. */
  const leafPaths: Array<{ path: readonly number[]; names: readonly string[] }> = [];
  walkMenuPaths(
    menuGroups,
    ({ group, path, names }) => {
      if ((group.items ?? []).some((ref) => targets.has(ref.itemId))) leafPaths.push({ path, names });
    },
    { includeGroup: notArchived },
  );

  const byId = new Map<number, IMenuGroup>();
  for (const g of menuGroups) if (g.menuGroupId != null) byId.set(g.menuGroupId, g);

  const order: number[] = [];
  const pathsById = new Map<number, string[]>();
  const excludedOrder: number[] = [];
  const excludedPath = new Map<number, string>();
  for (const { path, names } of leafPaths) {
    // Every group ABOVE the listing group; the listing group itself lists an
    // item, so it can never qualify.
    for (let i = 0; i < path.length - 1; i += 1) {
      const id = path[i]!;
      const group = byId.get(id);
      if (!group) continue;
      const text = names.slice(0, i + 1).join(MENU_PATH_SEPARATOR);
      if ((group.items ?? []).length > 0) {
        if (!excludedPath.has(id)) {
          excludedPath.set(id, text);
          excludedOrder.push(id);
        }
        continue;
      }
      const seen = pathsById.get(id);
      if (!seen) {
        pathsById.set(id, [text]);
        order.push(id);
      } else if (!seen.includes(text)) {
        seen.push(text);
      }
    }
  }

  // Walk order: sort by the position the group's first path was met in the
  // pre-order walk, so a root precedes its children.
  const firstSeen = new Map<number, number>();
  let n = 0;
  walkMenuPaths(
    menuGroups,
    ({ group }) => {
      const id = group.menuGroupId as number;
      if (!firstSeen.has(id)) firstSeen.set(id, n++);
    },
    { includeGroup: notArchived },
  );
  const byWalk = (a: number, b: number) => (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0);
  order.sort(byWalk);
  excludedOrder.sort(byWalk);

  return {
    eligible: order.map((id) => {
      const paths = pathsById.get(id)!;
      return { menuGroupId: id, name: byId.get(id)?.name ?? String(id), path: paths[0]!, paths };
    }),
    excluded: excludedOrder.map((id) => ({
      menuGroupId: id,
      name: byId.get(id)?.name ?? String(id),
      path: excludedPath.get(id)!,
      reason: 'lists_items' as const,
    })),
  };
}

/**
 * Of the menus in `pricedMenuGroupIds` (those whose reference for ONE modifier
 * group carries overrides), the ones whose prices can never ring: on EVERY
 * root path that reaches them, a menu above them is also priced, and the
 * ROOT→LEAF walk stops at that one first.
 *
 * A menu shadowed on only SOME paths is not returned: House of Pho's DRINKS
 * under a priced Happy Hour still rings at its own price on Lunch and Dinner.
 */
export function fullyShadowedPriceMenus(
  menuGroups: readonly IMenuGroup[],
  pricedMenuGroupIds: Iterable<number>,
): Set<number> {
  const priced = new Set(pricedMenuGroupIds);
  /** Priced menu → whether some path reaches it with nothing priced above. */
  const open = new Map<number, boolean>();
  walkMenuPaths(
    menuGroups,
    ({ group, path }) => {
      const id = group.menuGroupId as number;
      if (!priced.has(id)) return;
      const clear = path.slice(0, -1).every((above) => !priced.has(above));
      open.set(id, (open.get(id) ?? false) || clear);
    },
    { includeGroup: notArchived },
  );
  const out = new Set<number>();
  for (const [id, isOpen] of open) if (!isOpen) out.add(id);
  return out;
}

/**
 * A menu group's `modifierGroups` after an IMPORT names its modifier groups
 * (both import paths: the management client's `importEngine` and the server's
 * `MenuImportWriteService`).
 *
 * An import row lists modifier groups by NAME; it cannot express a per-menu
 * price. Replacing the array with the row's would silently delete every
 * per-menu price the restaurant typed — the bar's (Tito's +$1 at Happy Hour)
 * and the manager's own from "Configuration by Parent". So the row is
 * authoritative for WHICH groups it attaches, and prices ride along:
 *  - a group the row names keeps the stored reference's
 *    `modifierPriceOverrides`. It is now the row's attachment, so it is no
 *    longer a bar price-only reference (`barPriceRef` is dropped — the bar
 *    must never detach a group the import attached);
 *  - a stored reference the row does NOT name is carried forward, appended,
 *    when it carries prices or is the bar's (`barPriceRef`);
 *  - any other stored reference is replaced, as before.
 *
 * `changed` reports whether the result differs from `prior` (the caller moves
 * `modifierGroupsRev` exactly when it does).
 */
export function mergeImportedModifierGroupRefs(
  prior: readonly IModifierGroupReference[] | undefined,
  incoming: readonly IModifierGroupReference[],
): { refs: IModifierGroupReference[]; changed: boolean } {
  const stored = prior ?? [];
  const priced = (r: IModifierGroupReference) =>
    r.barPriceRef === true || (r.modifierPriceOverrides?.length ?? 0) > 0;
  const named = new Set(incoming.map((r) => r.modifierGroupId));

  const refs: IModifierGroupReference[] = incoming.map((r) => {
    const before = stored.find((s) => s.modifierGroupId === r.modifierGroupId);
    const overrides = before?.modifierPriceOverrides;
    if (!overrides || overrides.length === 0) return r;
    return { ...r, modifierPriceOverrides: overrides };
  });
  let position = refs.reduce((m, r) => Math.max(m, typeof r.position === 'number' ? r.position : -1), -1);
  for (const s of stored) {
    if (named.has(s.modifierGroupId) || !priced(s)) continue;
    position += 1;
    refs.push({ ...s, position });
  }
  return { refs, changed: canonical(refs) !== canonical(stored) };
}

/** Key-order-independent JSON, `undefined` fields dropped — Firestore hands keys back in its own order. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_k, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, (v as Record<string, unknown>)[k]]))
      : v,
  );
}

/** Some reference on this menu group is a bar price-only reference (`barPriceRef`). */
export function carriesBarPriceRef(modifierGroups: readonly IModifierGroupReference[] | undefined): boolean {
  return (modifierGroups ?? []).some((r) => r.barPriceRef === true);
}

type DirectItemsState = {
  readonly items?: ReadonlyArray<{ readonly itemId: number }>;
  readonly modifierGroups?: readonly IModifierGroupReference[];
};

/**
 * B3, after a bar save (design 2026-09-30) — ONE rule for every write path
 * (the menu editors through `updateMenuGroup`, both imports, bar-catalog
 * adoption, the Mixed Drinks items). A menu group's reference to a list
 * ATTACHES that list to every item the group lists directly, so a group that
 * carries a bar price-only reference must not gain items of its own: Happy
 * Hour's vodka prices would put the vodka list on them.
 *
 * Refused when, after the write, the group carries a bar reference AND
 *  - it lists an item id it did not list before (whatever the count — a swap
 *    adds an item too), or
 *  - the bar reference is new beside items already listed.
 * A group already in that state (from before this rule) keeps its existing
 * items: removing items, reordering them or editing anything else stays
 * allowed, so it can be fixed. `before` is undefined for a new group.
 */
export function barPricesRefuseDirectItems(before: DirectItemsState | undefined, after: DirectItemsState): boolean {
  if (!carriesBarPriceRef(after.modifierGroups)) return false;
  const afterIds = (after.items ?? []).map((i) => i.itemId);
  if (afterIds.length === 0) return false;
  const beforeIds = new Set((before?.items ?? []).map((i) => i.itemId));
  if (afterIds.some((id) => !beforeIds.has(id))) return true;
  return !carriesBarPriceRef(before?.modifierGroups);
}

/** What the refusal says — the same sentence on every write path. */
export function barPricesBlockDirectItemsMessage(menuName: string): string {
  return `${menuName} carries bar prices, so items listed directly in it would get the bar lists too. Put the items in a group under it.`;
}
